<template>
  <div class="call-popup-container">
    <!-- Active Call Popups - Stacked from bottom -->
    <transition-group name="popup-slide" tag="div" class="popups-wrapper">
      <!-- Outgoing Call Popup -->
      <CallPopup
        v-if="showOutgoingPopup"
        key="outgoing-call"
        :call="outgoingCallData"
        :index="0"
        class="popup-item"
      />

      <!-- Incoming Call Popup (only when NOT using the dialog) -->
      <CallPopup
        v-if="showIncomingPopup"
        key="incoming-call"
        :call="incomingCallData"
        :index="showOutgoingPopup ? 1 : 0"
        class="popup-item"
      />
    </transition-group>

    <!-- Save Contact Dialog - Shows after incoming call ends if number is unknown -->
    <SaveContactDialog
      v-model="saveContactDialogVisible"
      :phone="pendingContactPhone"
      :show-disposition-after="pendingDisposition"
    />

    <!-- SIP Disposition Dialog - Shows after call ends -->
    <SipDispositionDialog />
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import CallPopup from "./CallPopup.vue";
import SipDispositionDialog from "./SipDispositionDialog.vue";
import SaveContactDialog from "./SaveContactDialog.vue";

export default {
  name: "CallPopupContainer",

  components: {
    CallPopup,
    SipDispositionDialog,
    SaveContactDialog,
  },

  computed: {
    ...mapState("dialer", [
      "connectionStatus",
      "callStatus",
      "isInCall",
      "callDuration",
      "currentNumber",
      "currentContactName",
      "incomingCall",
      "incomingStatus",
      "incomingVisible",
      "showCallPopup",
      "callDirection",
      "showIncomingSaveContactDialog",
      "pendingContactPhone",
      "pendingDisposition",
    ]),
    ...mapGetters("dialer", [
      "isConnected",
      "hasIncomingCall",
      "incomingCallDetails",
    ]),

    // Show outgoing popup when there's an active outgoing call
    showOutgoingPopup() {
      // Show when:
      // 1. showCallPopup is true (set when makeCall is initiated)
      // 2. Call is in an active state
      // 3. It's an outgoing call direction
      const isActiveOutgoing =
        this.showCallPopup &&
        this.callDirection === "outgoing" &&
        ["calling", "ringing", "connected"].includes(this.callStatus);

      console.log("[CallPopupContainer] showOutgoingPopup check:", {
        showCallPopup: this.showCallPopup,
        callDirection: this.callDirection,
        callStatus: this.callStatus,
        result: isActiveOutgoing,
      });

      return isActiveOutgoing;
    },

    // Show incoming popup for ALL incoming call states (ringing, connecting, connected)
    // This replaces the full-screen IncomingCallDialog with the same popup style as outgoing calls
    showIncomingPopup() {
      // Show popup for any active incoming call state
      const isActiveIncoming =
        this.incomingVisible &&
        ["ringing", "connecting", "connected"].includes(this.incomingStatus) &&
        !this.showOutgoingPopup; // Don't show if we have an outgoing call

      console.log("[CallPopupContainer] showIncomingPopup check:", {
        incomingVisible: this.incomingVisible,
        incomingStatus: this.incomingStatus,
        showOutgoingPopup: this.showOutgoingPopup,
        result: isActiveIncoming,
      });

      return isActiveIncoming;
    },

    // Outgoing call data object for popup
    outgoingCallData() {
      return {
        id: "outgoing-" + Date.now(),
        direction: "outgoing",
        status: this.callStatus,
        number: this.currentNumber,
        contactName: this.currentContactName,
        duration: this.callDuration,
      };
    },

    // Incoming call data object for popup
    incomingCallData() {
      const details = this.incomingCallDetails || {};
      // Use displayNumber for anonymous callers (shows DID), otherwise use from/callerNumber
      const callerNumber =
        details.displayNumber || details.from || details.callerNumber || "";
      // Prioritize contactName from lookup, then callerName, then displayName
      const callerName =
        details.contactName || details.callerName || details.displayName || "";

      // For anonymous callers, show better display
      const isAnonymous = details.isAnonymous;
      const displayCallerName = isAnonymous
        ? details.did
          ? `Private (via ${details.did})`
          : "Private Caller"
        : callerName || callerNumber || "Unknown Caller";

      return {
        id: details.callId || details.id || "incoming-" + Date.now(),
        direction: "incoming",
        status: this.incomingStatus || "ringing",
        number: callerNumber,
        isAnonymous: isAnonymous,
        // Use contact name if found, otherwise caller number
        contactName: displayCallerName,
        callerName: displayCallerName,
        displayName: displayCallerName,
        duration: this.callDuration,
        did: details.did || details.normalizedDid || "",
        strategy: details.strategy || "",
        agent: details.agent || null,
        contactFound: details.contactFound,
        contactId: details.contactId,
      };
    },

    // Computed property with getter/setter for v-model on SaveContactDialog
    saveContactDialogVisible: {
      get() {
        return this.showIncomingSaveContactDialog;
      },
      set(value) {
        this.$store.commit(
          "dialer/SET_SHOW_INCOMING_SAVE_CONTACT_DIALOG",
          value,
        );
      },
    },
  },

  watch: {
    // Log state changes for debugging
    callStatus(newVal, oldVal) {
      console.log(
        "[CallPopupContainer] callStatus changed:",
        oldVal,
        "->",
        newVal,
      );
    },
    incomingStatus(newVal, oldVal) {
      console.log(
        "[CallPopupContainer] incomingStatus changed:",
        oldVal,
        "->",
        newVal,
      );
    },
    showIncomingSaveContactDialog(newVal) {
      console.log(
        "[CallPopupContainer] 📋 showIncomingSaveContactDialog:",
        newVal,
        "phone:",
        this.pendingContactPhone,
      );
    },
    incomingVisible(val) {
      console.log("[CallPopupContainer] incomingVisible:", val);
    },
    showCallPopup(val) {
      console.log("[CallPopupContainer] showCallPopup:", val);
    },
  },
};
</script>

<style scoped>
.call-popup-container {
  position: fixed;
  bottom: 160px;
  /* Position above the FAB and auto-dialer button */
  right: 24px;
  z-index: 999;
  display: flex;
  flex-direction: column-reverse;
  align-items: flex-end;
  pointer-events: none;
}

.popups-wrapper {
  display: flex;
  flex-direction: column-reverse;
  gap: 0;
}

.popup-item {
  pointer-events: auto;
}

/* Transition animations */
.popup-slide-enter-active,
.popup-slide-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.popup-slide-enter,
.popup-slide-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.9);
}

.popup-slide-move {
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Responsive */
@media (max-width: 768px) {
  .call-popup-container {
    bottom: 80px;
    right: 12px;
    left: 12px;
  }

  .popup-item {
    width: 100%;
  }
}

@media (max-width: 400px) {
  .call-popup-container {
    bottom: 70px;
    right: 8px;
    left: 8px;
  }
}
</style>
